/**
 * A subtle charging/draining mark beside the battery figure (Eric, 2026-10-30, mock approved:
 * "Yes, ship it"): ▲ in the battery band's colour while charging, ▼ in muted grey while draining,
 * nothing otherwise. The slot keeps its width either way so rows don't shift. The direction comes
 * from the server's live_status.battery.direction (docs/contracts/unit-live-status.md).
 */
import { cn } from '@/lib/utils';
import type { LegacyIconState } from '@/lib/legacyStatusIcons';

const TEXT: Record<NonNullable<LegacyIconState['batteryFill']>, string> = {
  'bg-success': 'text-success',
  'bg-warning': 'text-warning',
  'bg-error': 'text-error',
};

export function BatteryDirectionMark({ direction, fill, className }: {
  direction: LegacyIconState['direction'];
  fill: LegacyIconState['batteryFill'];
  className?: string;
}) {
  const label = direction === 'charging' ? 'Charging' : direction === 'draining' ? 'Draining' : undefined;
  const tone = direction === 'charging' && fill ? TEXT[fill] : 'text-muted-foreground';
  return (
    <span
      className={cn('inline-block w-2 text-center text-[8px] leading-none', tone, className)}
      title={label}
      aria-label={label}
      role={label ? 'img' : undefined}
    >
      {direction === 'charging' ? '▲' : direction === 'draining' ? '▼' : ''}
    </span>
  );
}
